import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import '../idb-heading/idb-heading';
import '../../base/outline-container/outline-container';
import '../idb-card-grid/idb-card-grid';
import { MobileController } from '../../controllers/mobile-controller';
import componentStyles from './idb-stat-bar.css.lit';
import { HeadingLevelType } from '../../base/outline-heading/config';
import { classMap } from 'lit/directives/class-map.js';

export type ColNum = 1 | 2 | 3 | 4;

export type Stat = {
  stat: string;
  desc: string;
};

export type ComponentColors =
  | 'blue-white'
  | 'white-blue'
  | 'white-black'
  | 'gray-blue'
  | 'light-blue-blue';

/**
 * The IDB Stat Bar component.
 * @element idb-stat-bar
 */
@customElement('idb-stat-bar')
export class IdbStatBar extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  private mobileController = new MobileController(this);

  /**
   * Section heading
   */
  @property({ type: String })
  heading: string;

  /**
   * The heading tag to apply: p | span | h1 | h2 | h3 | h4 | h5 | h6
   */
  @property({ type: String, reflect: true, attribute: 'level' })
  level: HeadingLevelType = 'h2';

  /**
   * Component Color options
   */
  @property({ type: String, attribute: 'component-color' })
  componentColor: ComponentColors;

  /**
   * Array of Stats objects
   */
  @property({ type: Array })
  stats: Stat[];

  render(): TemplateResult {
    const [lg, sm, def] = this.calculateGrid();

    const cardClasses = {
      [`idb-stat-bar-color--${this.componentColor}`]:
        this.componentColor !== undefined,
      'idb-stat-bar': true,
      'bg-container': true,
    };

    return html` <div class="${classMap(cardClasses)}">
      <outline-container ?full-bleed=${false}>
        ${this.headerTemplate()}
        <idb-card-grid
          columns-lg="${lg}"
          columns-sm="${sm}"
          columns-default="${def}"
          ?is-nested=${true}
          >${this.statsTemplate()}</idb-card-grid
        >
      </outline-container>
    </div>`;
  }

  headerTemplate = (): TemplateResult => {
    return html`<idb-heading
      level="${this.level}"
      level-size="${this.mobileController.isMobile ? '3xl' : '3xl'}"
      level-style="semibold"
      >${this.heading}</idb-heading
    >`;
  };

  statsTemplate = (): TemplateResult[] => {
    return this.stats
      ? this.stats.map(
          (stat: Stat) => html`
            <div class="stat-container">
              <p class="stat">${stat.stat}</p>
              <p class="stat-desc">${stat.desc}</p>
            </div>
          `
        )
      : [];
  };

  calculateGrid = (): [ColNum, ColNum, ColNum] => {
    const statCount = this.stats ? Object.entries(this.stats).length : 1;
    let colCount: [ColNum, ColNum, ColNum] = [4, 2, 2];

    switch (statCount) {
      case 1:
        colCount = [1, 1, 1];
        break;
      case 2:
        colCount = [2, 2, 2];
        break;
      case 3:
        colCount = [3, 1, 1];
        break;
      case 4:
        colCount = [4, 2, 2];
        break;
      default:
        break;
    }

    return colCount;
  };
}
